import DOMPurify, { type DOMPurify as Purifier } from 'dompurify';

const ALLOWED_TAGS = [
  'b',
  'i',
  'em',
  'strong',
  'a',
  'p',
  'div',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'img'
];

// sanitize-html's per-tag defaults. DOMPurify's ALLOWED_ATTR is global, so the
// hook in `getPurifier` re-scopes each attribute to its tag.
const TAG_ATTRIBUTES: Record<string, string[]> = {
  a: ['href', 'name', 'target'],
  img: ['src', 'srcset', 'alt', 'title', 'width', 'height', 'loading']
};
const ALLOWED_ATTRIBUTES = [...new Set(Object.values(TAG_ATTRIBUTES).flat())];

let purifier: Purifier | undefined;

// Own instance: mermaid shares the module singleton, and a hook added there
// would strip attributes off its SVG too.
const getPurifier = (): Purifier => {
  if (!purifier) {
    purifier = DOMPurify(window);
    purifier.addHook('uponSanitizeAttribute', (node, event) => {
      if (
        !TAG_ATTRIBUTES[node.nodeName.toLowerCase()]?.includes(event.attrName)
      ) {
        event.keepAttr = false;
      }
    });
  }
  return purifier;
};

const DANGEROUS_TAGS = [
  'script',
  'iframe',
  'object',
  'embed',
  'style',
  'link',
  'meta',
  'base'
];

/**
 * Detects if content looks like code/XML that should be auto-wrapped in a code block
 */
const looksLikeCode = (content: string): boolean => {
  // Don't treat already-fenced content as needing wrapping
  if (content.includes('```')) {
    return false;
  }

  // Extract all tags from content
  const tagRegex = /<\/?([a-zA-Z][a-zA-Z0-9-]*)[^>]*>/g;
  const matches = [...content.matchAll(tagRegex)];

  if (matches.length === 0) {
    return false;
  }

  const tags = matches.map((m) => m[1].toLowerCase());

  // Categorize tags
  const dangerous = tags.filter((t) => DANGEROUS_TAGS.includes(t));
  const allowed = tags.filter((t) => ALLOWED_TAGS.includes(t));
  const unknown = tags.filter(
    (t) => !dangerous.includes(t) && !allowed.includes(t)
  );

  // If has dangerous tags, don't treat as code (let sanitizer handle it)
  if (dangerous.length > 0) {
    return false;
  }

  // Don't auto-wrap single unknown tags (likely just needs sanitization)
  // Only auto-wrap if there are multiple unique unknown tags or multiple lines
  const hasMultipleLines = content.split('\n').length > 1;
  const hasMultipleUnknownTags = [...new Set(unknown)].length > 1;

  if (!hasMultipleLines && !hasMultipleUnknownTags) {
    return false;
  }

  // If mostly unknown tags (>50%), treat as code
  const unknownRatio = unknown.length / tags.length;
  return unknownRatio > 0.5;
};

export const standardSanitizeHtml = (dirty: string) => {
  // WARNING: without a DOM, DOMPurify returns its input unsanitized. Nothing
  // renders markdown server-side today; fail loudly if that changes.
  if (!DOMPurify.isSupported) {
    throw new Error('standardSanitizeHtml requires a DOM');
  }
  // Store code blocks (both inline and fenced) to preserve them
  const codeBlocks: string[] = [];
  const placeholder = '___CODE_BLOCK_PLACEHOLDER___';

  // Replace fenced code blocks first
  let content = dirty.replace(/```[\s\S]*?```/g, (match) => {
    codeBlocks.push(match);
    return `${placeholder}${codeBlocks.length - 1}`;
  });

  // Replace inline code blocks
  content = content.replace(/`([^`]*)`/g, (_, inlineContent) => {
    codeBlocks.push('`' + inlineContent + '`');
    return `${placeholder}${codeBlocks.length - 1}`;
  });

  // After extracting code blocks, check if remaining content looks like code
  // This prevents inline code like `<module>` from triggering the check
  if (looksLikeCode(content)) {
    content = '```\n' + content + '\n```';
  }

  // Disallowed tags are dropped, their text kept. PERF: DOMPurify (~8 KB gzip)
  // replaced sanitize-html, which pulled postcss + htmlparser2 (~61 KB) into every route.
  const sanitized = getPurifier().sanitize(content, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ALLOWED_ATTRIBUTES,
    ALLOW_DATA_ATTR: false,
    // sanitize-html's list; DOMPurify's default eats prose after an unclosed `<title>`.
    FORBID_CONTENTS: ['script', 'style', 'textarea', 'option', 'noscript']
  });

  // Restore code blocks
  return sanitized.replace(
    new RegExp(`${placeholder}(\\d+)`, 'g'),
    (_, index) => {
      return codeBlocks[parseInt(index)];
    }
  );
};
